import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface MaintenanceModalProps {
  isOpen: boolean;
  title?: string;
  description?: string;
  logoSrc: string;
  logoAlt?: string;
}

const MaintenanceModal: React.FC<MaintenanceModalProps> = ({
  isOpen,
  title = '「現在、ブイはメンテナンス中のため、データは一時的にご利用いただけません。数日以内に通常の機能が回復する予定です。ご理解のほど、よろしくお願いいたします。」',
  description = '',
  logoSrc,
  logoAlt = 'Maintenance Logo',
}) => {
  // Effect to lock the body scroll when the modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // If server-rendering or not open, bail out
  if (typeof document === 'undefined' || !isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="maintenance-overlay"
          data-testid="maintenance-modal"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 10, 20, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            overflow: 'hidden',
          }}
        >
          {/* Inner container for content animation */}
          <motion.div
            key="maintenance-content"
            initial={{ y: -50, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 120,
              damping: 15,
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              padding: '3rem 4rem',
              borderRadius: '16px',
              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              maxWidth: '650px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {/* Logo Display */}
            <motion.div
              // Add a subtle pulse animation to the logo
              animate={{ scale: [1, 1.03, 1] }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5,
              }}
              style={{ marginBottom: '2rem' }}
            >
              <img
                src={logoSrc}
                alt={logoAlt}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  maxHeight: '80px',
                  width: 'auto',
                  maxWidth: '100%',
                }}
              />
            </motion.div>

            {/* Title */}
            <h2
              style={{
                margin: '0 0 1rem 0',
                fontSize: '1.6rem',
                fontWeight: 700,
                color: '#ffffff',
                textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
                lineHeight: 1.4,
              }}
            >
              {title}
            </h2>

            {/* Description */}
            {description && (
              <p
                style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  color: '#e0e0e0',
                  lineHeight: 1.6,
                }}
              >
                {description}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default MaintenanceModal;
